import React, { useState } from "react";
import {
  Sheet,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Button,
  Divider,
  Select,
  Option,
  Snackbar,
} from "@mui/joy";
import {
  User,
  Globe,
  Calendar,
  Phone,
  Building2,
  CheckCircle,
} from "lucide-react";

const LeadForm = () => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [leadData, setLeadData] = useState({
    name: "",
    phone: "",
    source: "",
    company: "",
    appointmentDate: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLeadData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log("Lead Data:", leadData);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setIsSubmitting(true);
      setSnackbarSeverity("success");
      setSnackbarMessage("Form submitted successfully!");
      setLeadData({
        name: "",
        phone: "",
        source: "",
        company: "",
        appointmentDate: "",
      });
    } catch (error) {
      console.error("Error submitting lead:", error);
      setIsSubmitting(false);
      setSnackbarSeverity("danger");
      setSnackbarMessage("Form submission failed. Please try again.");
    } finally {
      setSnackbarOpen(true);
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet
      variant="outlined"
      sx={{
        maxWidth: "32rem",
        mx: "auto",
        borderRadius: "xl",
        boxShadow: "lg",
        backgroundColor: "white",
      }}
      className="p-8"
    >
      <div className="text-center mb-8">
        <Typography
          level="h2"
          className="text-gray-900 group-hover:text-blue-600 transition-colors duration-300"
        >
          Add New Lead
        </Typography>
        <Typography level="body-sm" className="text-gray-600 mt-2">
          Enter the details of your potential client
        </Typography>
      </div>

      <Divider className="my-6" />

      <form onSubmit={handleSubmit} className="space-y-6">
        <FormControl>
          <FormLabel>
            <div className="flex items-center gap-2 text-gray-700">
              <User size={18} />
              Lead Name
            </div>
          </FormLabel>
          <Input
            name="name"
            placeholder="Enter lead's full name"
            value={leadData.name}
            onChange={handleChange}
            required
            className="mt-1"
            sx={{
              "--Input-focusedThickness": "2px",
              "--Input-focusedHighlight": "rgb(59 130 246)",
              "&:hover": {
                borderColor: "neutral.outlinedHoverBorder",
              },
            }}
          />
        </FormControl>

        <FormControl>
          <FormLabel>
            <div className="flex items-center gap-2 text-gray-700">
              <Phone size={18} />
              Phone Number
            </div>
          </FormLabel>
          <Input
            name="phone"
            placeholder="+1 (555) 123-4567"
            value={leadData.phone}
            onChange={handleChange}
            required
            className="mt-1"
            sx={{
              "--Input-focusedThickness": "2px",
              "--Input-focusedHighlight": "rgb(59 130 246)",
              "&:hover": {
                borderColor: "neutral.outlinedHoverBorder",
              },
            }}
          />
        </FormControl>

        <FormControl>
          <FormLabel>
            <div className="flex items-center gap-2 text-gray-700">
              <Globe size={18} />
              Lead Source
            </div>
          </FormLabel>
          <Select
            name="source"
            placeholder="Select lead source"
            value={leadData.source}
            onChange={(_, value) =>
              setLeadData((prev) => ({ ...prev, source: value }))
            }
            required
            sx={{
              "--Select-focusedThickness": "2px",
              "--Select-focusedHighlight": "rgb(59 130 246)",
              "&:hover": {
                borderColor: "neutral.outlinedHoverBorder",
              },
            }}
          >
            <Option value="Facebook">Facebook</Option>
            <Option value="Instagram">Instagram</Option>
            <Option value="Website">Website</Option>
            <Option value="Referral">Referral</Option>
            <Option value="Direct">Direct Contact</Option>
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel>
            <div className="flex items-center gap-2 text-gray-700">
              <Building2 size={18} />
              Company (Optional)
            </div>
          </FormLabel>
          <Input
            name="company"
            placeholder="Enter company name"
            value={leadData.company}
            onChange={handleChange}
            className="mt-1"
            sx={{
              "--Input-focusedThickness": "2px",
              "--Input-focusedHighlight": "rgb(59 130 246)",
              "&:hover": {
                borderColor: "neutral.outlinedHoverBorder",
              },
            }}
          />
        </FormControl>

        <FormControl>
          <FormLabel>
            <div className="flex items-center gap-2 text-gray-700">
              <Calendar size={18} />
              Appointment Date
            </div>
          </FormLabel>
          <Input
            type="date"
            name="appointmentDate"
            value={leadData.appointmentDate}
            onChange={handleChange}
            required
            className="mt-1"
            sx={{
              "--Input-focusedThickness": "2px",
              "--Input-focusedHighlight": "rgb(59 130 246)",
              "&:hover": {
                borderColor: "neutral.outlinedHoverBorder",
              },
            }}
          />
        </FormControl>

        <Button
          type="submit"
          loading={isSubmitting}
          loadingPosition="start"
          fullWidth
          size="lg"
          startDecorator={!isSubmitting && <CheckCircle size={18} />}
          sx={{
            mt: 4,
            background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
            },
            transition: "all 0.2s ease-in-out"
          }}
        >
          {isSubmitting ? "Adding Lead..." : "Add Lead"}
        </Button>
      </form>
      <Snackbar
        color={snackbarSeverity}
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
      >
        {snackbarMessage}
      </Snackbar>
    </Sheet>
  );
};

export default LeadForm;
